import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import {
  LuTicket, LuArrowLeftRight, LuShuffle, LuRotateCcw,
  LuSettings2, LuShieldCheck, LuArrowRight, LuGauge, LuMapPin,
  LuCircleCheck,
} from 'react-icons/lu';

const IMG = {
  hero:     'https://sc04.alicdn.com/kf/HTB1y6oQlMfH8KJjy1zcq6ATzpXaG.jpg',
  checkin:  'https://skud.pro/wp-content/uploads/2025/07/e52fc5d3-8093-4809-9f28-8df2ed28b212.png',
  transfer: 'https://images.stockcake.com/public/0/6/e/06e8185b-d0f8-4180-9e7b-1dac7c8213f6_large/luggage-conveyor-belt-stockcake.jpg',
  sorting:  'https://www.daifuku.com/solution/assets/ogp/ap.jpg',
  carousel: 'https://i.pinimg.com/originals/09/6e/99/096e99a1628eb557894dbd98a8ccc5e2.jpg',
};

const TYPES = [
  {
    icon: <LuTicket size={26} />,
    iconBg: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
    img: IMG.checkin,
    badge: 'Departures', badgeClass: 'badge-blue',
    title: 'Check-in Conveyor',
    desc: `Located directly at the check-in desks, these belts are the passenger luggage's first contact point. They accept the bag, record its weight, and feed it into the main system toward security screening.`,
    features: ['Slow-speed for passenger safety','Integrated weight measurement','Opens to multiple downstream paths','Handles oversized items & pushchairs'],
    speed: '0.5 – 1.5 m/s', location: 'Check-in Hall',
    accentColor: 'rgba(124,58,237,.12)', accentBorder: 'rgba(124,58,237,.3)',
  },
  {
    icon: <LuArrowLeftRight size={26} />,
    iconBg: 'linear-gradient(135deg,#059669,#047857)',
    img: IMG.transfer,
    badge: 'Connections', badgeClass: 'badge-green',
    title: 'Transfer Conveyor',
    desc: 'The backbone of the baggage network. Transfer conveyors connect terminals, levels, and buildings — carrying bags across long distances at high speed through underground tunnels and enclosed bridges.',
    features: ['High-speed long-distance transport','Underground tunnel routing','Connects multiple airport terminals','Bi-directional flow capability'],
    speed: '5 – 10 m/s', location: 'Underground Tunnels',
    accentColor: 'rgba(5,150,105,.1)', accentBorder: 'rgba(5,150,105,.3)',
  },
  {
    icon: <LuShuffle size={26} />,
    iconBg: 'linear-gradient(135deg,#0891b2,#0e7490)',
    img: IMG.sorting,
    badge: 'Routing', badgeClass: 'badge-cyan',
    title: 'Sorting Conveyor',
    desc: "The intelligent heart of the system. Crossbelt sorters and tilt-tray units read each bag's RFID or barcode tag and divert it to the correct departure gate path. AI algorithms optimise routing in real time.",
    features: ['AI-controlled real-time routing','Crossbelt & tilt-tray technology','99.8%+ sorting accuracy','Processes 3,000+ bags/hour'],
    speed: '2 – 4 m/s', location: 'Sorting Hall',
    accentColor: 'rgba(8,145,178,.1)', accentBorder: 'rgba(8,145,178,.3)',
  },
  {
    icon: <LuRotateCcw size={26} />,
    iconBg: 'linear-gradient(135deg,#d97706,#b45309)',
    img: IMG.carousel,
    badge: 'Arrivals', badgeClass: 'badge-orange',
    title: 'Baggage Claim Carousel',
    desc: 'The final destination for arriving bags. The familiar rotating oval carousel delivers bags from arriving aircraft to the arrivals hall, where passengers collect their luggage after landing.',
    features: ['Oval or flat-plate belt design','Handles up to 600 bags/hour','Multiple carousels per terminal','Gentle low-damage handling speed'],
    speed: '0.3 – 0.5 m/s', location: 'Arrivals Hall',
    accentColor: 'rgba(217,119,6,.1)', accentBorder: 'rgba(217,119,6,.3)',
  },
];

const TABLE_ROWS = [
  ['Check-in Conveyor',       'Check-in Hall',    '0.5–1.5 m/s', 'Accepts bags from passengers', 'Integrated weight measurement'],
  ['Transfer Conveyor',       'Underground',      '5–10 m/s',    'Long-distance high-speed run',  'Bi-directional tunnel routing'],
  ['Sorting Conveyor',        'Sorting Hall',     '2–4 m/s',     'Routes bags to correct gates',  'AI-powered crossbelt diverters'],
  ['Baggage Claim Carousel',  'Arrivals Hall',    '0.3–0.5 m/s', 'Delivers bags to passengers',   'Continuous oval rotation'],
];

function Types() {
  return (
    <div className="page-enter">
      <PageHero
        tag="System Types"
        title={<>Types of Airport <span className="text-gradient">Conveyor Systems</span></>}
        subtitle="Four distinct conveyor types, each precisely engineered for a specific role in the end-to-end baggage journey — from check-in to aircraft and back."
        breadcrumb={[{ label: 'Conveyor Types' }]}
        bgColor={`radial-gradient(ellipse 65% 55% at 65% 35%, rgba(124,58,237,.08) 0%, transparent 60%),
                  radial-gradient(ellipse 50% 40% at 20% 75%, rgba(8,145,178,.06) 0%, transparent 55%), var(--bg)`}
      >
        <div className="page-hero-img-card">
          <img src={IMG.hero} alt="Airport baggage claim carousel" loading="lazy" />
        </div>
      </PageHero>

      {/* ── TYPE CARDS with images ── */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">Detailed Breakdown</div>
            <h2 className="section-title">Each Conveyor Type Explained</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Each type is optimised for its specific role — operating at different speeds,
              locations, and levels of automation within the airport.
            </p>
          </div>

          <div className="types-cards-list">
            {TYPES.map((t, i) => (
              <div
                key={i}
                className={`type-card-full reveal delay-${i + 1}`}
                style={{ '--accent-border': t.accentBorder }}
              >
                {/* Image */}
                <div className="type-card-img">
                  <img src={t.img} alt={t.title} loading="lazy" />
                  <div className="type-card-img-badge" style={{ background: t.iconBg }}>
                    {t.icon}
                  </div>
                </div>

                {/* Content */}
                <div className="type-card-content">
                  <div className="type-card-top">
                    <span className={`badge ${t.badgeClass}`}>{t.badge}</span>
                  </div>
                  <h3 className="type-card-title">{t.title}</h3>
                  <p className="type-card-desc">{t.desc}</p>

                  {/* Speed & Location chips */}
                  <div className="type-card-meta">
                    <div className="tcm-chip" style={{ background: t.accentColor, borderColor: t.accentBorder }}>
                      <LuGauge size={14} style={{ color: 'var(--primary)' }} />
                      <span><strong>Speed:</strong> {t.speed}</span>
                    </div>
                    <div className="tcm-chip" style={{ background: t.accentColor, borderColor: t.accentBorder }}>
                      <LuMapPin size={14} style={{ color: 'var(--primary)' }} />
                      <span><strong>Location:</strong> {t.location}</span>
                    </div>
                  </div>

                  {/* Feature list */}
                  <ul className="type-card-features">
                    {t.features.map((f, j) => (
                      <li key={j}>
                        <LuCircleCheck size={15} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ── COMPARISON TABLE ── */}
      <section className="page-section">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">Comparison</div>
            <h2 className="section-title">Side-by-Side Comparison</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              See how the four conveyor types differ across key operational parameters at a glance.
            </p>
          </div>

          <div className="reveal" style={{ overflowX: 'auto' }}>
            <table className="compare-table" aria-label="Conveyor types comparison">
              <thead>
                <tr>
                  {['Type', 'Location', 'Speed', 'Primary Role', 'Key Feature'].map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ color: j === 0 ? 'var(--text-2)' : 'var(--text-3)', fontWeight: j === 0 ? 700 : 400 }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="divider" />

      <section className="page-section-sm">
        <div className="container text-center reveal">
          <h2 className="section-title">Curious How They All Connect?</h2>
          <p className="section-sub" style={{ margin: '0 auto 32px' }}>
            Follow the full step-by-step journey from bag drop to aircraft loading and understand
            how these four systems work seamlessly together.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/how-it-works" className="btn btn-primary">
              <LuSettings2 size={17} /> Full Journey Walkthrough <LuArrowRight size={15} />
            </Link>
            <Link to="/safety" className="btn btn-outline">
              <LuShieldCheck size={17} /> Safety Systems
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Types;
